import React, { useRef, useState, useCallback, useMemo } from "react";
import AsyncSelect from "react-select/async";
import { useJsApiLoader } from "@react-google-maps/api";
import { customSelectStyles_dark, customSelectStyles_light } from "../ui/CustomSelect";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const libraries = ["places"];

const GooglePlacesSelect = ({ onPlaceSelect }) => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const [coordinates, setCoordinates] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const [selected, setSelected] = useState(null);

    const placesLibRef = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
        version: "weekly", // important for newer Places JS features
    });

    const getPlacesLib = useCallback(async () => {
        if (!window.google?.maps?.importLibrary) return null;
        if (placesLibRef.current) return placesLibRef.current;

        const lib = await window.google.maps.importLibrary("places");
        placesLibRef.current = lib;
        return lib;
    }, []);

    const loadOptions = async (inputValue, callback) => {
        const q = (inputValue || "").trim();
        if (!isLoaded || q.length < 2) return callback([]);

        try {
            const lib = await window.google.maps.importLibrary("places");

            // IMPORTANT: fetchAutocompleteSuggestions is a static function on the class
            const res = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: q,
                // Optional bias for Kenya:
                // includedRegionCodes: ["ke"],
            });

            console.log("fetchAutocompleteSuggestions:", res);

            const options = (res?.suggestions || [])
                .map((s) => s.placePrediction)
                .filter(Boolean)
                .map((p) => ({
                    label: [p.mainText?.text, p.secondaryText?.text].filter(Boolean).join(", "),
                    value: p.placeId,
                    placePrediction: p,
                }));

            callback(options);
        } catch (e) {
            console.error("Places suggestions failed:", e);
            callback([]);
        }
    };


    const handleChange = async (selectedOption) => {
        if (!selectedOption) {
            setSelectedAddress(null);
            setCoordinates(null);
            onPlaceSelect?.(null);
            return;
        }

        setSelectedAddress(selectedOption.label);

        try {
            const place = selectedOption.placePrediction?.toPlace?.();
            if (!place?.fetchFields) {
                console.log("toPlace/fetchFields not available");
                return;
            }

            await place.fetchFields({ fields: ["location"] });

            const loc = place.location;
            const coords = loc ? { lat: loc.lat(), lng: loc.lng() } : null;

            setCoordinates(coords);

            onPlaceSelect?.({
                label: selectedOption.label,
                value: selectedOption.value,
                coordinates: coords,
            });
        } catch (e) {
            console.error("Place fetchFields failed:", e);
        }
    };


    const selectStyles = useMemo(
        () => ({
            ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }),
        [currentTheme]
    );

    if (loadError) return <div>Error loading Google Maps</div>;
    if (!isLoaded) return <div>Loading...</div>;

    return (
        <AsyncSelect
            cacheOptions
            defaultOptions={false}
            value={selected}
            loadOptions={loadOptions}
            width="450px"
            onChange={handleChange}
            isClearable
            menuPortalTarget={document.body}
            styles={selectStyles}
            placeholder={t("enterAddress")}
        />
    );
};

export default GooglePlacesSelect;
